/**
 * ChartEditorPanel — slide-in chart authoring surface.
 *
 * Layout
 * ──────
 *  ┌────────────────── header (title + Save / Cancel) ────────────────┐
 *  ├──────────────── top row ────────────────────────────────────────┤
 *  │ ┌─ left ──────────────┐  ┌─ right ───────────────────────────┐ │
 *  │ │ Schema/table picker │  │ Preview table — query result      │ │
 *  │ │ SQL textarea + Run  │  │                                   │ │
 *  │ └─────────────────────┘  └───────────────────────────────────┘ │
 *  ├──────────────── bottom row ─────────────────────────────────────┤
 *  │             Plotly chart builder (react-chart-editor)            │
 *  └─────────────────────────────────────────────────────────────────┘
 *
 *  The panel slides in from the left (translateX) and takes 100% of
 *  the dashboard area. There is no built-in chat — instead the panel
 *  registers itself with DashboardContext so the existing Data Chat
 *  agent on the same page can read its state and apply edits.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Input, Select, Table, Alert, Spin, Typography,
  Segmented, Modal, Dropdown, Tooltip, ColorPicker, message as antMessage,
} from 'antd';
import {
  CloseOutlined, PlayCircleOutlined, SaveOutlined,
  BarChartOutlined, BarsOutlined, TableOutlined, NumberOutlined, PlusOutlined,
  LineChartOutlined, AreaChartOutlined, PieChartOutlined, DotChartOutlined,
  HeatMapOutlined, BoxPlotOutlined,
  FullscreenOutlined, FullscreenExitOutlined,
} from '@ant-design/icons';
import plotly from 'plotly.js/dist/plotly';
import PlotlyEditor from 'react-chart-editor';
import 'react-chart-editor/lib/react-chart-editor.css';

import { useDashboard } from '../contexts/DashboardContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  listSchemas, describeTable, buildExplicitSelect,
} from '../services/chartService';
import { executeDashboardSQL } from '../services/dashboardService';
import { buildEditorDataSources, hydrateFigure, normalizeFigure } from '../utils/plotlyData';
import { getChartData, setChartData } from '../utils/chartDataCache';
import { computeMetric, formatValue } from './MetricCard';
import { ICON_PRESETS, COLOR_SWATCHES, MetricIcon } from './metricIcons';
import MiniSparkline from './MiniSparkline';
import {
  parseWhereFilters, rebuildSqlWithFilters, WhereFilterChips,
} from './DeliveryFilters';
import { wrapWithYAggregation } from '../utils/yAggregation';
import SqlEditor from './SqlEditor';
import './dashboard-tokens.css';

const { Text } = Typography;

const DEFAULT_FIGURE = { data: [], layout: { autosize: true }, frames: [] };
const PREVIEW_ROW_LIMIT = 200;

// Convert an Error (possibly augmented by buildErrorFromResponse with
// .code/.hint/.identifier) into a structured shape consumed by the
// runError Alert. Falls back to parsing a JSON string that may have
// leaked through from a non-structured backend response.
function formatRunError(err, fallback = 'Query failed.') {
  if (!err) return { message: fallback };
  let message = err.message || fallback;
  let code = err.code || null;
  let hint = err.hint || null;
  let identifier = err.identifier || null;
  // Backward-compat: if the message is a JSON blob, parse it.
  if (typeof message === 'string' && message.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(message);
      const src = parsed?.detail && typeof parsed.detail === 'object'
        ? parsed.detail : parsed;
      if (src && typeof src === 'object') {
        if (typeof src.message === 'string') message = src.message;
        code = code || src.error || src.code || null;
        hint = hint || src.hint || null;
        identifier = identifier || src.identifier || null;
      }
    } catch { /* not JSON — leave as-is */ }
  }
  return { message, code, hint, identifier };
}

// Aggregation functions exposed in the Y-axis aggregation control.
const Y_AGG_OPTIONS = [
  { value: 'none',           label: 'None' },
  { value: 'sum',            label: 'SUM' },
  { value: 'avg',            label: 'AVG' },
  { value: 'min',            label: 'MIN' },
  { value: 'max',            label: 'MAX' },
  { value: 'count',          label: 'COUNT' },
  { value: 'count_distinct', label: 'COUNT DISTINCT' },
];

// Time-bucket units for per-group-by-column DATE_TRUNC. Selecting anything
// other than `none` rewrites the GROUP BY column as DATE_TRUNC('<unit>', col).
const Y_AGG_TIME_OPTIONS = [
  { value: 'none',    label: 'raw' },
  { value: 'year',    label: 'Year' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'month',   label: 'Month' },
  { value: 'week',    label: 'Week' },
  { value: 'day',     label: 'Day' },
  { value: 'hour',    label: 'Hour' },
  { value: 'minute',  label: 'Minute' },
];

// ── Editor layout presets ─────────────────────────────────────────────
// Each preset arranges the three logical regions (sql, data, editor)
// differently. Sizes (in % of parent axis) live in `paneSizes` and are
// persisted per-user in localStorage.
//
// Each preset ships with a tiny SVG glyph that mirrors the actual
// region arrangement so the dropdown reads at a glance:
//   • amber rect = SQL pane     • blue = Data preview     • green = chart
const LAYOUT_ICON_BASE = {
  width: 22, height: 16, viewBox: '0 0 22 16',
  fill: 'none', xmlns: 'http://www.w3.org/2000/svg',
  style: { flexShrink: 0, display: 'block' },
};
const LAYOUT_ICON_COLORS = {
  sql:   { fill: '#fef3c7', stroke: '#d97706' }, // amber
  data:  { fill: '#dbeafe', stroke: '#2563eb' }, // blue
  chart: { fill: '#dcfce7', stroke: '#16a34a' }, // green
  frame: '#9ca3af',
};
const cell = (key, x, y, w, h) => (
  <rect
    key={`${key}-${x}-${y}`}
    x={x} y={y} width={w} height={h}
    rx="1.5"
    fill={LAYOUT_ICON_COLORS[key].fill}
    stroke={LAYOUT_ICON_COLORS[key].stroke}
    strokeWidth="1"
  />
);
const LayoutIconHorizontal = () => (
  <svg {...LAYOUT_ICON_BASE} aria-hidden="true">
    {/* top row split SQL | Data, bottom row chart full width */}
    {cell('sql',   1, 1, 8,  6)}
    {cell('data', 11, 1, 10, 6)}
    {cell('chart', 1, 9, 20, 6)}
  </svg>
);
const LayoutIconVertical = () => (
  <svg {...LAYOUT_ICON_BASE} aria-hidden="true">
    {/* SQL full-height left, right column splits Data / chart */}
    {cell('sql',   1, 1, 8, 14)}
    {cell('data', 11, 1, 10, 6)}
    {cell('chart',11, 9, 10, 6)}
  </svg>
);
const LayoutIconThreePane = () => (
  <svg {...LAYOUT_ICON_BASE} aria-hidden="true">
    {/* Three columns side by side */}
    {cell('sql',   1, 1, 6, 14)}
    {cell('data',  9, 1, 6, 14)}
    {cell('chart',17, 1, 4, 14)}
  </svg>
);
const LayoutIconStacked = () => (
  <svg {...LAYOUT_ICON_BASE} aria-hidden="true">
    {/* Three full-width rows */}
    {cell('sql',   1, 1,  20, 4)}
    {cell('data',  1, 6,  20, 4)}
    {cell('chart', 1, 11, 20, 4)}
  </svg>
);

const LAYOUT_OPTIONS = [
  { value: 'horizontal', label: 'Horizontal split',  description: 'SQL | Data on top, chart on bottom', Icon: LayoutIconHorizontal },
  { value: 'vertical',   label: 'Vertical split',    description: 'SQL on left, Data + chart on right', Icon: LayoutIconVertical },
  { value: 'three-pane', label: 'Three-pane row',    description: 'SQL | Data | chart, single row',     Icon: LayoutIconThreePane },
  { value: 'stacked',    label: 'Stacked',           description: 'SQL / Data / chart, full-width rows', Icon: LayoutIconStacked },
];

const DEFAULT_PANE_SIZES = {
  // horizontal: top row height + sql width within top row
  horizontal: { topRow: 45, sqlPane: 35 },
  // vertical: sql width + data height within right column
  vertical:   { sqlPane: 35, dataPane: 40 },
  // three-pane: sql width + data width (chart fills remainder)
  'three-pane': { sqlPane: 28, dataPane: 32 },
  // stacked: sql height + data height (chart fills remainder)
  stacked:    { sqlPane: 30, dataPane: 30 },
};

const LS_LAYOUT_KEY = 'dx-editor-layout';
const LS_SIZES_KEY  = 'dx-editor-pane-sizes';

function loadLayoutPref() {
  try {
    const v = localStorage.getItem(LS_LAYOUT_KEY);
    if (LAYOUT_OPTIONS.some((o) => o.value === v)) return v;
  } catch { /* ignore */ }
  return 'horizontal';
}
function loadSizesPref() {
  try {
    const raw = localStorage.getItem(LS_SIZES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Merge with defaults so missing keys (newer layouts) still resolve.
      return {
        horizontal:   { ...DEFAULT_PANE_SIZES.horizontal,   ...(parsed.horizontal || {}) },
        vertical:     { ...DEFAULT_PANE_SIZES.vertical,     ...(parsed.vertical   || {}) },
        'three-pane': { ...DEFAULT_PANE_SIZES['three-pane'],...(parsed['three-pane'] || {}) },
        stacked:      { ...DEFAULT_PANE_SIZES.stacked,      ...(parsed.stacked || {}) },
      };
    }
  } catch { /* ignore */ }
  return DEFAULT_PANE_SIZES;
}
function persistSizes(sizes) {
  try { localStorage.setItem(LS_SIZES_KEY, JSON.stringify(sizes)); } catch { /* ignore */ }
}

/**
 * Splitter — draggable handle between two flex children.
 *
 * `axis='h'` → vertical bar that resizes left/right siblings (col-resize).
 * `axis='v'` → horizontal bar that resizes top/bottom siblings (row-resize).
 *
 * `containerRef` must point to the parent flex container; the bar reads the
 * parent's bounding rect to translate mouse delta into a percentage and
 * forwards it via `onChange(pct)`. Pct is clamped to [10, 90].
 */
function Splitter({ axis, containerRef, onChange }) {
  const handleMouseDown = (e) => {
    e.preventDefault();
    const node = containerRef.current;
    if (!node) return;
    document.body.style.cursor = axis === 'h' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    const onMove = (e2) => {
      const rect = node.getBoundingClientRect();
      const pct = axis === 'h'
        ? ((e2.clientX - rect.left) / rect.width) * 100
        : ((e2.clientY - rect.top) / rect.height) * 100;
      const clamped = Math.max(10, Math.min(90, pct));
      onChange(Number(clamped.toFixed(1)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  return (
    <div
      className={`dx-splitter dx-splitter-${axis}`}
      role="separator"
      aria-orientation={axis === 'h' ? 'vertical' : 'horizontal'}
      onMouseDown={handleMouseDown}
    />
  );
}

export default function ChartEditorPanel({ open, onClose, onSaved, existingItem, initialItemType }) {
  const { addItem, updateItem, registerChartEditor, activeDashboardId, isFullscreen, toggleFullscreen } = useDashboard();
  const { themes: themeMap, currentTheme } = useTheme() || {};
  const themeMode = themeMap?.[currentTheme]?.mode === 'dark' ? 'dark' : 'light';

  // ── Item-type toggle: chart | table | metric ──────────────────────
  // 'chart'  → Plotly builder (bottom half).
  // 'table'  → no builder; the saved SQL is rendered as a paginated
  //            antd Table on the dashboard.
  // 'metric' → pick a column + aggregation; the saved item shows a
  //            single big number on the dashboard.
  const [itemType, setItemType] = useState('chart');

  // ── Editor pane layout (preset + per-pane sizes, persisted) ───────
  const [editorLayout, setEditorLayout] = useState(loadLayoutPref);
  const [paneSizes, setPaneSizes] = useState(loadSizesPref);
  useEffect(() => {
    try { localStorage.setItem(LS_LAYOUT_KEY, editorLayout); } catch { /* ignore */ }
  }, [editorLayout]);
  const setSize = useCallback((layout, key, pct) => {
    setPaneSizes((prev) => {
      const next = { ...prev, [layout]: { ...prev[layout], [key]: pct } };
      persistSizes(next);
      return next;
    });
  }, []);
  // Refs for splitter parents (one per layout boundary).
  const topRowRef       = useRef(null); // horizontal: outer column
  const topRowInnerRef  = useRef(null); // horizontal: top row (sql|data)
  const vSplitOuterRef  = useRef(null); // vertical: outer row (sql|right)
  const vSplitInnerRef  = useRef(null); // vertical: right column (data/chart)
  const threePaneRef    = useRef(null); // three-pane: outer row
  const stackedRef      = useRef(null); // stacked: outer column

  const [title, setTitle] = useState('Untitled chart');
  const [sql, setSql] = useState('');
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  const [schemas, setSchemas] = useState([]);
  const [schemaName, setSchemaName] = useState(null);
  const [tableName, setTableName] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);

  const [figure, setFigure] = useState(DEFAULT_FIGURE);

  // ── WHERE filter bar state ────────────────────────────────────────
  // Auto-generated from the current SQL: every recognized WHERE
  // predicate becomes an editable chip. Changing a chip rebuilds the
  // SQL on the fly and re-runs it (similar to the chat delivery bar).
  // The values are *not* written back into the SQL textarea while the
  // user edits — only on save (so the persisted sql_query reflects
  // whatever filter values are currently active).
  const [filterValues, setFilterValues] = useState({});
  // Per-column disable map: { [columnName]: true } means the WHERE
  // predicate for that column is omitted when rebuilding the SQL.
  const [disabledFilters, setDisabledFilters] = useState({});

  // ── Y-axis aggregation toolbar state ──────────────────────────────
  // Independent of the figure: when applied, it wraps the current SQL
  // in an outer aggregating SELECT and replaces the textarea contents
  // (so saving captures the rewritten SQL). After Apply, `yAgg` is
  // reset back to 'none' — it's a one-shot transform, not a live one.
  const [yAggCol, setYAggCol] = useState(null);
  const [yAggFunc, setYAggFunc] = useState('none');
  const [yAggGroupBy, setYAggGroupBy] = useState([]);
  // Per-group-by-column time-bucket: { [columnName]: 'day' | 'week' | ... }.
  // Columns absent from this map are grouped on their raw value. Buckets
  // emit DATE_TRUNC('<unit>', "<col>") in the rewritten SQL.
  const [yAggTimeUnits, setYAggTimeUnits] = useState({});
  // Aggregation panel is collapsed by default — the form expands inline
  // when the user clicks the "+ Aggregate" button at the bottom of the
  // SQL section. Auto-collapses after a successful Apply.
  const [yAggOpen, setYAggOpen] = useState(false);

  // Bumped whenever the figure is replaced from outside the
  // PlotlyEditor (initial rehydrate, chat-assist apply, etc.). Used as
  // a `key` on <PlotlyEditor> to force a clean remount because
  // react-chart-editor only reads `data`/`layout`/`frames` on mount
  // — subsequent prop changes are ignored.
  const [figureRev, setFigureRev] = useState(0);
  const bumpFigureRev = () => setFigureRev((v) => v + 1);

  // Metric editor state — mirrored into chart_config.metric on save.
  const [metricCfg, setMetricCfg] = useState({
    column: null,
    agg: 'first',     // first | sum | avg | min | max | count
    format: 'number', // number | usd | percent
    prefix: '',
    suffix: '',
    label: '',
  });

  // ── Dirty tracking ────────────────────────────────────────────────
  // Snapshot of state at open-time. Anything mutated past this baseline
  // is "dirty" and triggers the confirm-on-close dialog.
  const baselineRef = useRef(null);
  const isDirty = useCallback(() => {
    const b = baselineRef.current;
    if (!b) return false;
    if (b.itemType !== itemType) return true;
    if (b.title !== title) return true;
    if (b.sql !== sql) return true;
    if (JSON.stringify(b.figure) !== JSON.stringify({ data: figure.data, layout: figure.layout })) return true;
    if (JSON.stringify(b.metricCfg) !== JSON.stringify(metricCfg)) return true;
    return false;
  }, [itemType, title, sql, figure, metricCfg]);

  // ── Rehydrate state on open ────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const DEFAULT_METRIC_CFG = {
      column: null, agg: 'first', format: 'number', prefix: '', suffix: '', label: '',
      color: null, icon: 'auto',
    };

    let nextItemType;
    let nextTitle;
    let nextSql;
    let nextFigure;
    // Always reset metricCfg to the default first; only override when a metric
    // config is actually present on the item. This prevents stale metric
    // settings from leaking onto a re-opened *chart* item, which used to keep
    // `isDirty()` permanently true.
    let nextMetricCfg = { ...DEFAULT_METRIC_CFG };

    if (existingItem) {
      nextItemType = existingItem.item_type || 'chart';
      nextTitle = existingItem.title || 'Untitled';
      nextSql = existingItem.sql_query || '';
      // Hydrate the saved chart_config against the dashboard card's
      // cached query result BEFORE handing it to react-chart-editor.
      // The persisted spec uses *src column references plus our own
      // primitives (splitBy, marker.colorMap) that the editor doesn't
      // understand. Without expansion:
      //   - the preview renders blank until the user touches something
      //     (no inline data on the traces),
      //   - the editor's trace/style panels show defaults instead of
      //     the configured values (it can't read the primitives), and
      //   - saving from the editor discards them (round-trip data loss).
      // Expanding now produces concrete inline traces with concrete
      // colors that the editor can faithfully display and save.
      const cachedPreview = getChartData(existingItem.id);
      const rawFigure = {
        data: existingItem.chart_config?.data || [],
        layout: existingItem.chart_config?.layout || { autosize: true },
        frames: existingItem.chart_config?.frames || [],
      };
      if (cachedPreview?.columns?.length && cachedPreview?.rows?.length) {
        const ds = buildEditorDataSources(cachedPreview.columns, cachedPreview.rows).dataSources;
        nextFigure = hydrateFigure(normalizeFigure(rawFigure, ds), ds);
      } else {
        nextFigure = rawFigure;
      }
      const m = existingItem.chart_config?.metric;
      if (m) {
        nextMetricCfg = {
          column: m.column ?? null,
          agg: m.agg || 'first',
          format: m.format || 'number',
          prefix: m.prefix || '',
          suffix: m.suffix || '',
          label: m.label || '',
          spark: m.spark || null,
          color: m.color || null,
          icon: m.icon || 'auto',
        };
      }
    } else {
      const startType = ['chart', 'table', 'metric'].includes(initialItemType) ? initialItemType : 'chart';
      nextItemType = startType;
      nextTitle = startType === 'metric' ? 'Untitled metric' : startType === 'table' ? 'Untitled table' : 'Untitled chart';
      nextSql = '';
      nextFigure = DEFAULT_FIGURE;
    }

    setItemType(nextItemType);
    setTitle(nextTitle);
    setSql(nextSql);
    setFigure(nextFigure);
    bumpFigureRev();
    setMetricCfg(nextMetricCfg);

    setSchemaName(null);
    setTableName(null);
    // Reuse the data the dashboard card already fetched, if available.
    // Falls back to an auto-run below when the cache is empty (e.g. the
    // user clicked Edit before the card finished its initial fetch).
    const cached = existingItem ? getChartData(existingItem.id) : null;
    setColumns(cached?.columns || []);
    setRows(cached?.rows || []);
    setRunError(null);
    setFilterValues({});
    setDisabledFilters({});
    setYAggCol(null);
    setYAggFunc('none');
    setYAggGroupBy([]);
    setYAggTimeUnits({});
    setYAggOpen(false);

    // Snapshot the baseline synchronously from the SAME source of truth we
    // used for the state setters above. The previous setTimeout(0) version
    // raced fast user input — anything typed before the timeout fired got
    // baked into the baseline and the dirty flag stayed false.
    baselineRef.current = {
      itemType: nextItemType,
      title: nextTitle,
      sql: nextSql,
      figure: {
        data: nextFigure.data || [],
        layout: nextFigure.layout || { autosize: true },
      },
      metricCfg: { ...nextMetricCfg },
    };
  }, [open, existingItem, initialItemType]);

  // Close handler with a dirty-guard.
  const requestClose = useCallback(() => {
    if (!isDirty()) { onClose?.(); return; }
    Modal.confirm({
      title: 'Discard your changes?',
      content: 'You have unsaved edits. Closing now will discard them.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: () => onClose?.(),
    });
  }, [isDirty, onClose]);

  // Esc key triggers the dirty-guarded close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); requestClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, requestClose]);

  // ── Load schemas ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await listSchemas();
        if (!cancelled) setSchemas(resp?.schemas || []);
      } catch (err) {
        if (!cancelled) antMessage.error(`Failed to load schemas: ${err?.message || err}`);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // ── Auto-run on edit-open when cache is cold ───────────────────────
  // Normally the dashboard card has already fetched data and seeded
  // `chartDataCache`, so the editor reuses it without a refetch. If the
  // user clicked Edit before the card finished its initial load, the
  // cache is empty — in that case fetch once so the chart-editor data
  // sources, table preview, metric column picker, etc. are populated.
  // Skips entirely for *new* items (no `existingItem`) and items with
  // no SQL.
  useEffect(() => {
    if (!open) return undefined;
    if (!existingItem?.sql_query) return undefined;
    const cached = getChartData(existingItem.id);
    if (cached?.columns?.length) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await executeDashboardSQL(existingItem.sql_query, PREVIEW_ROW_LIMIT);
        if (cancelled) return;
        const payload = resp?.data || resp;
        const cols = payload?.columns || [];
        const rws = payload?.rows || [];
        if (!cols.length) return;
        setColumns(cols);
        setRows(rws);
        // Seed the shared cache so re-opening the editor stays cheap.
        setChartData(existingItem.id, { columns: cols, rows: rws });
        // Now that we have data sources, expand the saved figure's
        // primitives (splitBy / marker.colorMap / *src) into concrete
        // traces so the preview paints on first render and the
        // editor's panels reflect the saved configuration.
        const ds = buildEditorDataSources(cols, rws).dataSources;
        const raw = {
          data: existingItem.chart_config?.data || [],
          layout: existingItem.chart_config?.layout || { autosize: true },
          frames: existingItem.chart_config?.frames || [],
        };
        setFigure(hydrateFigure(normalizeFigure(raw, ds), ds));
        bumpFigureRev();
      } catch (err) {
        if (!cancelled) setRunError(formatRunError(err, 'Failed to load data.'));
      }
    })();
    return () => { cancelled = true; };
  }, [open, existingItem?.id, existingItem?.sql_query]);

  // ── Register with DashboardContext so the page-level Data Chat can
  //    read state and apply edits while the editor is open. ──────────
  //
  // The handle reads live state from a ref so we only register/teardown
  // once per panel open — previous version re-registered on every keystroke
  // which made `chartEditorOpen` flicker for downstream consumers.
  const liveStateRef = useRef({ sql, columns, rows, figure, itemType, metricCfg });
  useEffect(() => {
    liveStateRef.current = { sql, columns, rows, figure, itemType, metricCfg };
  });
  // Latest setSqlAndRun closure (depends on handleRun which depends on schema state).
  const setSqlAndRunRef = useRef(null);
  useEffect(() => {
    setSqlAndRunRef.current = async (next) => {
      const trimmed = (next || '').trim();
      if (!trimmed) return null;
      setSql(trimmed);
      const result = await handleRun(trimmed);
      return result;
    };
  });

  useEffect(() => {
    if (!open) return undefined;
    const handle = {
      getState: () => ({ ...liveStateRef.current }),
      setSql: (next) => setSql(next),
      setFigure: (next) => {
        const { columns: cols, rows: rws } = liveStateRef.current;
        const ds = buildEditorDataSources(cols, rws).dataSources;
        const raw = {
          data: next?.data || [],
          layout: next?.layout || { autosize: true },
          frames: next?.frames || [],
        };
        const hydrated = hydrateFigure(normalizeFigure(raw, ds), ds);
        setFigure(hydrated);
        bumpFigureRev();
      },
      setMetricCfg: (patch) => {
        if (patch && typeof patch === 'object') {
          setMetricCfg((prev) => ({ ...prev, ...patch }));
        }
      },
      setItemType: (next) => {
        if (['chart', 'table', 'metric'].includes(next)) setItemType(next);
      },
      setSqlAndRun: (next) => setSqlAndRunRef.current?.(next),
      hydrateAndSetFigure: (next, opts) => {
        const live = liveStateRef.current;
        const cols = opts?.columns || live.columns;
        const rws = opts?.rows || live.rows;
        const ds = buildEditorDataSources(cols, rws).dataSources;
        const raw = {
          data: next?.data || [],
          layout: next?.layout || { autosize: true },
          frames: next?.frames || [],
        };
        const hydrated = hydrateFigure(normalizeFigure(raw, ds), ds);
        setFigure(hydrated);
        bumpFigureRev();
      },
    };
    registerChartEditor(handle);
    return () => registerChartEditor(null);
  }, [open, registerChartEditor]);

  const tableOptions = useMemo(() => {
    const s = schemas.find((x) => x.name === schemaName);
    return (s?.tables || []).map((t) => ({ value: t, label: t }));
  }, [schemas, schemaName]);

  const replaceSqlFromTable = useCallback(async (table) => {
    if (!schemaName || !table) return;
    setTableLoading(true);
    try {
      const desc = await describeTable(schemaName, table);
      const cols = (desc?.columns || []).map((c) => c.name || c);
      setSql(buildExplicitSelect(schemaName, table, cols, PREVIEW_ROW_LIMIT));
    } catch (err) {
      antMessage.warning(`Could not describe table: ${err?.message || err}`);
      setSql(buildExplicitSelect(schemaName, table, [], PREVIEW_ROW_LIMIT));
    } finally {
      setTableLoading(false);
    }
  }, [schemaName]);

  const handlePickTable = async (table) => {
    setTableName(table);
    if (!schemaName || !table) return;
    // Guard: if the user has already hand-written SQL, ask before clobbering.
    const existing = (sql || '').trim();
    if (existing.length > 0) {
      Modal.confirm({
        title: 'Replace your SQL?',
        content: `Picking "${table}" will replace your current query with a starter SELECT against that table. Continue?`,
        okText: 'Replace',
        cancelText: 'Keep current SQL',
        onOk: () => replaceSqlFromTable(table),
      });
      return;
    }
    await replaceSqlFromTable(table);
  };

  const handleRun = async (sqlOverride) => {
    const sqlToRun = (sqlOverride ?? sql ?? '').trim();
    if (!sqlToRun) { setRunError({ message: 'Enter a SQL SELECT statement.' }); return null; }
    setRunning(true);
    setRunError(null);
    try {
      const resp = await executeDashboardSQL(sqlToRun, PREVIEW_ROW_LIMIT);
      const payload = resp?.data || resp;
      if (!payload?.columns) throw new Error('Query returned no data.');
      const cols = payload.columns || [];
      const rws = payload.rows || [];
      setColumns(cols);
      setRows(rws);
      return { columns: cols, rows: rws, rowCount: rws.length };
    } catch (err) {
      // Surface the failure to the UI via the runError Alert. We do
      // NOT re-throw: several callers (Ctrl+Enter, Run button) invoke
      // handleRun without awaiting, and an unhandled rejection there
      // would trip the CRA runtime-error overlay even though the
      // error is already shown in the panel.
      setRunError(formatRunError(err));
      return null;
    } finally {
      setRunning(false);
    }
  };

  const editorDataSources = useMemo(() => {
    if (!columns.length) return { dataSources: {}, dataSourceOptions: [] };
    return buildEditorDataSources(columns, rows);
  }, [columns, rows]);

  // ── Parsed WHERE filters derived from the current SQL ─────────────
  // (memoized so the chip row reference is stable as long as the SQL
  // text is.)
  const parsedFilters = useMemo(() => parseWhereFilters(sql), [sql]);
  const hasFilters = useMemo(
    () => Object.keys(parsedFilters).length > 0,
    [parsedFilters],
  );

  // ── Initialize filterValues from parsed defaults whenever the SQL
  // changes. Existing user selections for columns that still exist are
  // preserved (so editing the SQL textarea doesn't blow away the chip
  // selections unless the predicate itself was removed).
  useEffect(() => {
    if (!open) return;
    setFilterValues((prev) => {
      const next = {};
      for (const [id, def] of Object.entries(parsedFilters)) {
        next[id] = prev[id] ?? def.values;
      }
      return next;
    });
  }, [parsedFilters, open]);

  // ── Re-execute the SQL whenever a chip changes ────────────────────
  // We rebuild the SQL on every filter change and run it. Note: the
  // `sql` state is *not* mutated — we keep the user's textarea text as
  // the canonical source. The rebuilt SQL is captured at save-time.
  const filterAbortRef = useRef(null);
  const runWithFilters = useCallback(async (nextValues, nextDisabled) => {
    if (!sql.trim()) return;
    if (filterAbortRef.current) filterAbortRef.current.abort();
    const controller = new AbortController();
    filterAbortRef.current = controller;
    const effectiveDisabled = nextDisabled !== undefined ? nextDisabled : disabledFilters;
    const rebuilt = rebuildSqlWithFilters(sql, parsedFilters, nextValues, effectiveDisabled);
    setRunning(true);
    setRunError(null);
    try {
      const resp = await executeDashboardSQL(
        rebuilt, PREVIEW_ROW_LIMIT, rebuilt !== sql,
        { signal: controller.signal },
      );
      const payload = resp?.data || resp;
      if (!payload?.columns) throw new Error('Query returned no data.');
      setColumns(payload.columns || []);
      setRows(payload.rows || []);
    } catch (err) {
      if (err?.name !== 'AbortError') setRunError(formatRunError(err));
    } finally {
      setRunning(false);
    }
  }, [sql, parsedFilters, disabledFilters]);

  const handleFilterChange = (id, vals) => {
    const next = { ...filterValues, [id]: vals };
    setFilterValues(next);
    runWithFilters(next, disabledFilters).catch(() => {});
  };
  const handleToggleFilterDisabled = (id, nextDisabled) => {
    const next = { ...disabledFilters };
    if (nextDisabled) next[id] = true; else delete next[id];
    setDisabledFilters(next);
    runWithFilters(filterValues, next).catch(() => {});
  };
  const handleFilterReset = () => {
    const reset = Object.fromEntries(
      Object.entries(parsedFilters).map(([id, def]) => [id, def.values]),
    );
    setFilterValues(reset);
    setDisabledFilters({});
    runWithFilters(reset, {}).catch(() => {});
  };

  // ── Apply Y-axis aggregation: wrap current SQL and re-run ─────────
  const handleApplyYAgg = async () => {
    if (!yAggCol || yAggFunc === 'none') {
      antMessage.info('Pick a Y column and an aggregation function.');
      return;
    }
    if (!sql.trim()) {
      antMessage.warning('Run a base query first.');
      return;
    }
    const groupEntries = (yAggGroupBy || []).map((c) => {
      const u = yAggTimeUnits[c];
      return u && u !== 'none' ? { column: c, timeUnit: u } : c;
    });
    const wrapped = wrapWithYAggregation(
      // Apply the aggregation on top of the *currently active* (filtered) SQL.
      rebuildSqlWithFilters(sql, parsedFilters, filterValues, disabledFilters),
      yAggCol, yAggFunc, groupEntries,
    );
    setSql(wrapped);
    // Reset filter values — the rewritten SQL has different parsed predicates.
    setFilterValues({});
    setDisabledFilters({});
    setYAggFunc('none');
    const result = await handleRun(wrapped);
    if (result) {
      antMessage.success(`Applied ${yAggFunc.toUpperCase()} to ${yAggCol}.`);
      setYAggOpen(false);
    }
    // On failure, handleRun already populated runError → Alert shows.
  };

  const handleSave = async () => {
    if (!sql.trim()) { antMessage.warning('Provide a SQL query.'); return; }
    let chart_config;
    if (itemType === 'chart') {
      if (!figure?.data?.length) { antMessage.warning('Add at least one trace to the chart.'); return; }
      chart_config = {
        data: figure.data,
        layout: figure.layout || {},
        frames: figure.frames || [],
      };
    } else if (itemType === 'metric') {
      if (metricCfg.agg !== 'count' && !metricCfg.column) {
        antMessage.warning('Pick a column for the metric.');
        return;
      }
      chart_config = { metric: { ...metricCfg } };
    } else {
      // table — no extra config needed
      chart_config = null;
    }
    // Bake current chip selections into the saved sql_query so the
    // dashboard card's WhereFilterChips defaults match what the
    // builder is showing right now. Disabled chips are saved as `1=1`
    // so the persisted query no longer applies those predicates.
    const savedSql = rebuildSqlWithFilters(sql, parsedFilters, filterValues, disabledFilters).trim();
    try {
      const payload = {
        item_type: itemType,
        title: title.trim() || 'Untitled',
        sql_query: savedSql,
        chart_config,
        // Persist to the currently-viewed dashboard. Without this the
        // backend falls back to the user's default dashboard, so the
        // new card appears optimistically but vanishes on refresh.
        dashboard_id: activeDashboardId,
      };
      // For NEW items, default to a comfortable half-width / 6-row tile.
      // (Existing items keep whatever size the user already gave them.)
      if (!existingItem?.id) {
        payload.grid_w = 6;
        payload.grid_h = 6;
      }
      const saved = existingItem?.id
        ? await updateItem(existingItem.id, payload)
        : await addItem(payload);
      antMessage.success(existingItem ? 'Item updated' : 'Item saved');
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      antMessage.error(`Save failed: ${err?.message || err}`);
    }
  };

  const previewColumns = useMemo(() => {
    // Size each column to fit its widest sample (header or a row), clamped to a tight range.
    // Smaller defaults than antd's auto so more columns fit in view at once.
    const SAMPLE = 50;          // rows to inspect for sizing
    const MIN_W  = 64;
    const MAX_W  = 200;
    const PX_PER_CHAR = 6.7;    // approx for 11.5px monospace-ish content
    return columns.map((c, ci) => {
      let widest = (c || '').length;
      const limit = Math.min(SAMPLE, rows.length);
      for (let i = 0; i < limit; i++) {
        const v = rows[i]?.[ci];
        const s = v == null ? 4 : String(v).length;
        if (s > widest) widest = s;
        if (widest >= 30) break;
      }
      const px = Math.round(widest * PX_PER_CHAR + 18);
      return {
        title: c,
        dataIndex: c,
        key: c,
        ellipsis: true,
        width: Math.min(MAX_W, Math.max(MIN_W, px)),
      };
    });
  }, [columns, rows]);
  const previewData = useMemo(
    () => rows.slice(0, 200).map((r, i) => {
      const o = { key: i };
      columns.forEach((c, idx) => { o[c] = r[idx]; });
      return o;
    }),
    [columns, rows],
  );

  // ── Region content blocks (layout-agnostic) ────────────────────────
  // Each region renders just its content; the outer wrapping div with
  // sizing is supplied by the layout renderer further down so the same
  // SQL/Data/Editor blocks can be arranged differently per preset.
  const sqlRegionContent = (
    <>
      <div style={{
        padding: '8px 10px',
        borderBottom: '1px solid var(--border-color, #f0f0f0)',
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        fontSize: 11,
        flexShrink: 0,
      }}>
        <span style={{ color: 'var(--theme-textSecondary, #6b7280)', fontSize: 11, flexShrink: 0 }}>Source</span>
        <Select
          showSearch
          placeholder="Schema"
          value={schemaName}
          onChange={(v) => { setSchemaName(v); setTableName(null); }}
          options={schemas.map((s) => ({ value: s.name, label: s.name }))}
          size="small"
          style={{ flex: 1, minWidth: 80, fontSize: 11 }}
          popupMatchSelectWidth={false}
        />
        <Select
          showSearch
          placeholder="Table"
          value={tableName}
          onChange={handlePickTable}
          options={tableOptions}
          disabled={!schemaName}
          loading={tableLoading}
          size="small"
          style={{ flex: 1.4, minWidth: 100, fontSize: 11 }}
          popupMatchSelectWidth={false}
        />
      </div>
      <div style={{
        flex: 1, minHeight: 0,
        padding: 12,
        display: 'flex', flexDirection: 'column', gap: 8,
        overflow: 'auto',
      }}>
        <SqlEditor
          value={sql}
          onChange={setSql}
          onRun={() => handleRun()}
          onSave={handleSave}
          variant={themeMode}
          rowsHint={columns.length ? `${rows.length} rows × ${columns.length} cols` : null}
          placeholder='SELECT col1, col2 FROM "schema"."table" WHERE ... LIMIT 200'
          toolbar={
            <Button
              size="small"
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={running}
              onClick={() => handleRun()}
              style={{ height: 22, fontSize: 11 }}
            >
              Run
            </Button>
          }
        />
        {runError && (
          <Alert
            type="error"
            showIcon
            message={
              runError?.code === 'PERMISSION_DENIED'
                ? `Access denied${runError.identifier ? ` to ${runError.identifier}` : ''}`
                : (runError?.message || 'Query failed.')
            }
            description={
              runError?.code === 'PERMISSION_DENIED' ? (
                <>
                  <div>{runError.message}</div>
                  {runError.hint && (
                    <div style={{ marginTop: 4, opacity: 0.85 }}>
                      <strong>Hint:</strong> {runError.hint}
                    </div>
                  )}
                </>
              ) : (runError?.hint || undefined)
            }
          />
        )}
        {hasFilters && (
          <WhereFilterChips
            parsedFilters={parsedFilters}
            filterValues={filterValues}
            disabledFilters={disabledFilters}
            onToggleDisabled={handleToggleFilterDisabled}
            data={{ columns, rows }}
            onFilterChange={handleFilterChange}
            onReset={handleFilterReset}
            className="sql-where-filter-bar sql-where-filter-bar--editor"
          />
        )}
        {columns.length > 0 && !yAggOpen && (
          <Button
            size="small"
            type="text"
            icon={<PlusOutlined />}
            onClick={() => setYAggOpen(true)}
            style={{
              alignSelf: 'flex-start',
              height: 24,
              fontSize: 11,
              padding: '0 6px',
              color: 'var(--theme-textSecondary, #6b7280)',
            }}
          >
            Aggregate
          </Button>
        )}
        {columns.length > 0 && yAggOpen && (
          <div style={{
            border: '1px solid var(--border-color, #e5e7eb)',
            borderRadius: 6,
            padding: 10,
            background: 'var(--theme-cardBgMuted, #fafafa)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 12 }}>Aggregate</Text>
              <Button
                size="small"
                type="text"
                icon={<CloseOutlined style={{ fontSize: 11 }} />}
                onClick={() => setYAggOpen(false)}
                style={{ height: 20, width: 20, minWidth: 20, padding: 0, lineHeight: 1 }}
                aria-label="Close aggregate panel"
              />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Select
                size="small"
                placeholder="Column"
                value={yAggCol}
                onChange={setYAggCol}
                options={columns.map((c) => ({ value: c, label: c }))}
                style={{ flex: 2, minWidth: 0 }}
                showSearch
                allowClear
              />
              <Select
                size="small"
                value={yAggFunc}
                onChange={setYAggFunc}
                options={Y_AGG_OPTIONS}
                style={{ flex: 1, minWidth: 0 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text type="secondary" style={{ fontSize: 11, flexShrink: 0, width: 56 }}>
                Group by
              </Text>
              <Select
                size="small"
                mode="multiple"
                placeholder="(optional)"
                value={yAggGroupBy}
                onChange={(next) => {
                  setYAggGroupBy(next);
                  setYAggTimeUnits((prev) => {
                    const keep = new Set(next);
                    const out = {};
                    for (const k of Object.keys(prev)) if (keep.has(k)) out[k] = prev[k];
                    return out;
                  });
                }}
                options={columns.filter((c) => c !== yAggCol).map((c) => ({ value: c, label: c }))}
                style={{ flex: 1, minWidth: 0 }}
                maxTagCount="responsive"
                allowClear
              />
            </div>
            {yAggGroupBy.length > 0 && (
              <div style={{
                marginLeft: 12,
                padding: '6px 8px',
                background: 'var(--theme-cardBg, #fff)',
                border: '1px dashed var(--border-color, #d4d8de)',
                borderRadius: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Time bucket</Text>
                {yAggGroupBy.map((col) => (
                  <div key={col} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Text
                      style={{
                        fontSize: 11, flex: 1, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                      title={col}
                    >
                      {col}
                    </Text>
                    <Select
                      size="small"
                      value={yAggTimeUnits[col] || 'none'}
                      onChange={(v) => setYAggTimeUnits((prev) => ({ ...prev, [col]: v }))}
                      options={Y_AGG_TIME_OPTIONS}
                      style={{ width: 86 }}
                    />
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                type="primary"
                disabled={!yAggCol || yAggFunc === 'none'}
                onClick={handleApplyYAgg}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const dataRegionContent = (
    <>
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-color, #f0f0f0)', flexShrink: 0 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {columns.length
            ? `Data — ${rows.length} rows × ${columns.length} cols`
            : 'Data (run query to populate)'}
        </Text>
      </div>
      <div className="dx-data-preview" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 8 }}>
        {running ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : columns.length ? (
          <Table
            size="small"
            columns={previewColumns}
            dataSource={previewData}
            pagination={false}
            scroll={{ x: 'max-content', y: '100%' }}
            tableLayout="fixed"
          />
        ) : (
          <div style={{ color: 'var(--theme-textSecondary, #999)', fontSize: 12, padding: 24, textAlign: 'center' }}>
            Pick a table or run a SQL query to load data.
          </div>
        )}
      </div>
    </>
  );

  const editorRegionContent = (
    itemType === 'chart' ? (
      columns.length === 0 ? (
        <div className="dx-builder-empty">
          <div className="msg">
            <b>No data yet.</b> Run the SQL above to fetch rows, then pick a chart type below.
          </div>
          <div className="templates-wrap">
            <QuickChartTemplates columns={[]} disabled />
          </div>
        </div>
      ) : (figure?.data?.length === 0 ? (
        <QuickChartTemplates
          columns={columns}
          onPick={(spec) => {
            const ds = buildEditorDataSources(columns, rows).dataSources;
            const hydrated = hydrateFigure(normalizeFigure(spec, ds), ds);
            setFigure(hydrated);
            bumpFigureRev();
          }}
          onCustomize={() => {
            const ds = buildEditorDataSources(columns, rows).dataSources;
            const minimal = { data: [{ type: 'bar', xsrc: columns[0], ysrc: columns[1] || columns[0] }], layout: { autosize: true } };
            const hydrated = hydrateFigure(normalizeFigure(minimal, ds), ds);
            setFigure(hydrated);
            bumpFigureRev();
          }}
        />
      ) :
        <PlotlyEditor
          key={`plotly-editor-${figureRev}`}
          data={figure.data}
          layout={figure.layout}
          frames={figure.frames}
          dataSources={editorDataSources.dataSources}
          dataSourceOptions={editorDataSources.dataSourceOptions}
          plotly={plotly}
          onUpdate={(data, layout, frames) => setFigure({ data, layout, frames })}
          useResizeHandler
          advancedTraceTypeSelector
        />
      )
    ) : itemType === 'table' ? (
      <div style={{ padding: 24, color: 'var(--theme-textSecondary, #666)', textAlign: 'center', fontSize: 13 }}>
        <TableOutlined style={{ fontSize: 32, color: 'var(--theme-textIcon, #bbb)', marginBottom: 12 }} />
        <div>The dashboard card will render the SQL above as a paginated table.</div>
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--theme-textSecondary, #999)' }}>
          Tip: use <code>LIMIT</code> in your query to keep cards snappy.
        </div>
      </div>
    ) : (
      <MetricEditor
        columns={columns}
        rows={rows}
        cfg={metricCfg}
        onChange={setMetricCfg}
      />
    )
  );

  // Wraps a region's content in a sized flex column. The wrapper takes
  // the size from the parent layout (px or pct via inline `style`); the
  // content fills it. The chart region also gets `chart-editor-host`
  // so the Plotly editor's nested CSS overrides keep applying.
  const regionWrap = (key, content, extraStyle) => (
    <div
      key={key}
      className={
        `dx-editor-region dx-editor-region-${key}` +
        (key === 'chart' ? ' chart-editor-host' : '')
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        background: 'var(--theme-cardBg, #fff)',
        ...extraStyle,
      }}
    >
      {content}
    </div>
  );

  return (
    <div
      className="chart-editor-panel"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'var(--theme-cardBg, #fff)',
        zIndex: 5,
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 240ms ease',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: open ? '4px 0 16px rgba(0,0,0,0.08)' : 'none',
      }}
      aria-hidden={!open}
    >
      {/* Header — 3-column grid keeps the type switcher visually centered
          regardless of how wide the title input or right-side actions are. */}
      <div className="dx-editor-head">
        {/* Left: title label + input */}
        <div className="dx-editor-head-left">
          <Text strong style={{ flexShrink: 0 }}>Title</Text>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder={existingItem ? 'Edit item' : 'Untitled'}
            style={{ maxWidth: 320 }}
          />
        </div>

        {/* Center: type switcher */}
        <div className="dx-editor-head-center">
          <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>Type</Text>
          <Segmented
            className="dx-type-switcher"
            value={itemType}
            onChange={setItemType}
            options={[
              { label: 'Chart',  value: 'chart',  icon: <BarChartOutlined /> },
              { label: 'Metric', value: 'metric', icon: <NumberOutlined /> },
              { label: 'Table',  value: 'table',  icon: <TableOutlined /> },
            ]}
          />
        </div>

        {/* Right: dirty indicator + Layout + Fullscreen toggle + Cancel/Save */}
        <div className="dx-editor-head-right">
          {isDirty() && (
            <span style={{ fontSize: 11, color: 'var(--theme-textSecondary)', fontStyle: 'italic' }}>
              Unsaved changes
            </span>
          )}
          <Dropdown
            trigger={['click']}
            menu={{
              selectable: true,
              selectedKeys: [editorLayout],
              items: LAYOUT_OPTIONS.map((o) => ({
                key: o.value,
                label: (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, lineHeight: 1.25 }}>
                    <o.Icon />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 500 }}>{o.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--theme-textSecondary, #888)' }}>{o.description}</span>
                    </div>
                  </div>
                ),
              })),
              onClick: ({ key }) => setEditorLayout(key),
            }}
          >
            <Tooltip title="Editor layout">
              <Button>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {(() => {
                    const ActiveIcon = (LAYOUT_OPTIONS.find((o) => o.value === editorLayout) || LAYOUT_OPTIONS[0]).Icon;
                    return <ActiveIcon />;
                  })()}
                  Layout
                </span>
              </Button>
            </Tooltip>
          </Dropdown>
          <Tooltip title={isFullscreen ? 'Exit full-screen (show chat alongside)' : 'Full-screen the dashboard editor'}>
            <Button
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => { if (typeof toggleFullscreen === 'function') toggleFullscreen(); }}
              aria-label={isFullscreen ? 'Exit full-screen' : 'Enter full-screen'}
            />
          </Tooltip>
          <Button icon={<CloseOutlined />} onClick={requestClose}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>

      {/* ── Body: layout-aware region renderer ──────────────────────
          Three regions (sql / data / editor) arranged per the active
          preset. Each pair is separated by a draggable Splitter that
          updates `paneSizes[layout]` (persisted in localStorage). */}
      {editorLayout === 'horizontal' && (
        <div ref={topRowRef} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div
            ref={topRowInnerRef}
            style={{
              height: `${paneSizes.horizontal.topRow}%`,
              minHeight: 200,
              display: 'flex',
              borderBottom: '1px solid var(--border-color, #e5e7eb)',
            }}
          >
            {regionWrap('sql', sqlRegionContent, {
              width: `${paneSizes.horizontal.sqlPane}%`,
              minWidth: 220,
              borderRight: '1px solid var(--border-color, #e5e7eb)',
            })}
            <Splitter axis="h" containerRef={topRowInnerRef}
              onChange={(p) => setSize('horizontal', 'sqlPane', p)} />
            {regionWrap('data', dataRegionContent, { flex: 1 })}
          </div>
          <Splitter axis="v" containerRef={topRowRef}
            onChange={(p) => setSize('horizontal', 'topRow', p)} />
          {regionWrap('chart', editorRegionContent, { flex: 1 })}
        </div>
      )}

      {editorLayout === 'vertical' && (
        <div ref={vSplitOuterRef} style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {regionWrap('sql', sqlRegionContent, {
            width: `${paneSizes.vertical.sqlPane}%`,
            minWidth: 240,
            borderRight: '1px solid var(--border-color, #e5e7eb)',
          })}
          <Splitter axis="h" containerRef={vSplitOuterRef}
            onChange={(p) => setSize('vertical', 'sqlPane', p)} />
          <div
            ref={vSplitInnerRef}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}
          >
            {regionWrap('data', dataRegionContent, {
              height: `${paneSizes.vertical.dataPane}%`,
              minHeight: 140,
              borderBottom: '1px solid var(--border-color, #e5e7eb)',
            })}
            <Splitter axis="v" containerRef={vSplitInnerRef}
              onChange={(p) => setSize('vertical', 'dataPane', p)} />
            {regionWrap('chart', editorRegionContent, { flex: 1 })}
          </div>
        </div>
      )}

      {editorLayout === 'three-pane' && (
        <div ref={threePaneRef} style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {regionWrap('sql', sqlRegionContent, {
            width: `${paneSizes['three-pane'].sqlPane}%`,
            minWidth: 220,
            borderRight: '1px solid var(--border-color, #e5e7eb)',
          })}
          <Splitter axis="h" containerRef={threePaneRef}
            onChange={(p) => setSize('three-pane', 'sqlPane', p)} />
          {regionWrap('data', dataRegionContent, {
            width: `${paneSizes['three-pane'].dataPane}%`,
            minWidth: 220,
            borderRight: '1px solid var(--border-color, #e5e7eb)',
          })}
          <Splitter axis="h" containerRef={threePaneRef}
            onChange={(p) => {
              // splitter pos is from the container's left edge; subtract
              // sqlPane width to get the data-pane percentage.
              const next = Math.max(10, p - paneSizes['three-pane'].sqlPane);
              setSize('three-pane', 'dataPane', next);
            }} />
          {regionWrap('chart', editorRegionContent, { flex: 1 })}
        </div>
      )}

      {editorLayout === 'stacked' && (
        <div ref={stackedRef} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {regionWrap('sql', sqlRegionContent, {
            height: `${paneSizes.stacked.sqlPane}%`,
            minHeight: 140,
            borderBottom: '1px solid var(--border-color, #e5e7eb)',
          })}
          <Splitter axis="v" containerRef={stackedRef}
            onChange={(p) => setSize('stacked', 'sqlPane', p)} />
          {regionWrap('data', dataRegionContent, {
            height: `${paneSizes.stacked.dataPane}%`,
            minHeight: 120,
            borderBottom: '1px solid var(--border-color, #e5e7eb)',
          })}
          <Splitter axis="v" containerRef={stackedRef}
            onChange={(p) => {
              const next = Math.max(10, p - paneSizes.stacked.sqlPane);
              setSize('stacked', 'dataPane', next);
            }} />
          {regionWrap('chart', editorRegionContent, { flex: 1 })}
        </div>
      )}
    </div>
  );
}

/**
 * MetricEditor — bottom-half UI when itemType === 'metric'.
 * Lets the user pick a column + aggregation + format and shows a
 * live preview of the resulting big-number tile.
 */
function MetricEditor({ columns, rows, cfg, onChange }) {
  const set = (patch) => onChange({ ...cfg, ...patch });
  const { value, error } = useMemo(
    () => (columns.length ? computeMetric(columns, rows, cfg) : { value: null, error: 'Run a query above to preview your metric.' }),
    [columns, rows, cfg],
  );
  const sparkSeries = useMemo(() => {
    if (!columns.length || !rows.length) return null;
    const colName = cfg?.spark;
    if (!colName) return null;
    const idx = columns.indexOf(colName);
    if (idx < 0) return null;
    const series = rows.map((r) => Number(r[idx])).filter((v) => !Number.isNaN(v));
    return series.length >= 2 ? series : null;
  }, [columns, rows, cfg?.spark]);

  const aggOptions = [
    { value: 'first',          label: 'First' },
    { value: 'sum',            label: 'Sum' },
    { value: 'avg',            label: 'Avg' },
    { value: 'min',            label: 'Min' },
    { value: 'max',            label: 'Max' },
    { value: 'count',          label: 'Count' },
    { value: 'count_distinct', label: 'Count Distinct' },
  ];
  const formatOptions = [
    { value: 'number',  label: 'Number' },
    { value: 'compact', label: 'Compact' },
    { value: 'usd',     label: 'USD' },
    { value: 'percent', label: 'Percent' },
  ];

  return (
    <div className="dx-metric-editor">
      {/* Form column */}
      <div className="dx-metric-form">
        <div className="dx-metric-section">
          <div className="dx-metric-section-h">Aggregation</div>
          <Segmented
            value={cfg.agg}
            onChange={(v) => set({ agg: v })}
            options={aggOptions}
            className="dx-metric-segmented"
          />
        </div>

        {cfg.agg !== 'count' && (
          <div className="dx-metric-section">
            <div className="dx-metric-section-h">Column</div>
            <Select
              placeholder="Pick a column"
              value={cfg.column}
              onChange={(v) => set({ column: v })}
              options={columns.map((c) => ({ value: c, label: c }))}
              disabled={!columns.length}
              showSearch
              style={{ width: '100%' }}
            />
          </div>
        )}

        <div className="dx-metric-section">
          <div className="dx-metric-section-h">Format</div>
          <Segmented
            value={cfg.format || 'number'}
            onChange={(v) => set({ format: v })}
            options={formatOptions}
            className="dx-metric-segmented"
          />
        </div>

        <div className="dx-metric-section">
          <div className="dx-metric-section-h">
            Sparkline <span className="dx-metric-hint">— optional trend column</span>
          </div>
          <Select
            placeholder="(none)"
            value={cfg.spark || undefined}
            onChange={(v) => set({ spark: v || null })}
            options={[{ value: '', label: '(none)' }, ...columns.map((c) => ({ value: c, label: c }))]}
            allowClear
            style={{ width: '100%' }}
          />
        </div>

        <div className="dx-metric-section">
          <div className="dx-metric-section-h">Label, prefix &amp; suffix</div>
          <Input
            value={cfg.label}
            maxLength={60}
            onChange={(e) => set({ label: e.target.value })}
            placeholder="Label (e.g. Active users)"
            style={{ marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              value={cfg.prefix}
              maxLength={6}
              onChange={(e) => set({ prefix: e.target.value })}
              placeholder="Prefix ($)"
              style={{ flex: 1 }}
            />
            <Input
              value={cfg.suffix}
              maxLength={12}
              onChange={(e) => set({ suffix: e.target.value })}
              placeholder="Suffix (users)"
              style={{ flex: 1 }}
            />
          </div>
        </div>

        <div className="dx-metric-section">
          <div className="dx-metric-section-h">Color</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {COLOR_SWATCHES.map((sw) => {
              const selected = (cfg.color || null) === sw.value;
              const isDefault = sw.value === null;
              return (
                <Tooltip key={sw.label} title={sw.label}>
                  <button
                    type="button"
                    aria-label={sw.label}
                    onClick={() => set({ color: sw.value })}
                    style={{
                      width: 24, height: 24, borderRadius: '50%',
                      border: selected ? '2px solid #1677ff' : '1px solid #d9d9d9',
                      background: isDefault
                        ? 'linear-gradient(135deg, #f0f0f0 50%, #fff 50%)'
                        : sw.value,
                      cursor: 'pointer', padding: 0,
                    }}
                  />
                </Tooltip>
              );
            })}
            <ColorPicker
              value={cfg.color || '#1677ff'}
              onChange={(c) => set({ color: c.toHexString() })}
              size="small"
            />
            {cfg.color && (
              <Button size="small" type="link" onClick={() => set({ color: null })} style={{ padding: '0 4px' }}>
                Reset
              </Button>
            )}
          </div>
        </div>

        <div className="dx-metric-section">
          <div className="dx-metric-section-h">
            Icon <span className="dx-metric-hint">— auto picks one from the format</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['auto', 'none'].map((k) => (
              <Button
                key={k}
                size="small"
                type={(cfg.icon || 'auto') === k ? 'primary' : 'default'}
                onClick={() => set({ icon: k })}
              >
                {k === 'auto' ? 'Auto' : 'None'}
              </Button>
            ))}
            {Object.entries(ICON_PRESETS).map(([key, { label, Component }]) => {
              const selected = cfg.icon === key;
              return (
                <Tooltip key={key} title={label}>
                  <Button
                    size="small"
                    type={selected ? 'primary' : 'default'}
                    onClick={() => set({ icon: key })}
                    icon={<Component />}
                  />
                </Tooltip>
              );
            })}
          </div>
        </div>
      </div>

      {/* Preview column — mirrors the dashboard MetricCard tile */}
      <div className="dx-metric-preview-area">
        <div className="dx-metric-preview-card">
          {error ? (
            <div className="dx-metric-preview-empty">
              <NumberOutlined style={{ fontSize: 36, opacity: 0.3 }} />
              <div>{error}</div>
            </div>
          ) : (
            <>
              {cfg.label && <div className="dx-metric-preview-label">{cfg.label}</div>}
              <div
                className="dx-metric-preview-value"
                style={{
                  color: cfg.color || undefined,
                  display: 'inline-flex', alignItems: 'center', gap: 12,
                }}
              >
                <MetricIcon cfg={cfg} style={{ fontSize: '0.7em', opacity: 0.85 }} />
                <span>{formatValue(value, cfg)}</span>
              </div>
              {sparkSeries && (
                <div className="dx-metric-preview-spark">
                  <MiniSparkline values={sparkSeries} height={36} />
                </div>
              )}
            </>
          )}
        </div>
        <div className="dx-metric-preview-caption">Live preview</div>
      </div>
    </div>
  );
}

/**
 * QuickChartTemplates — picker shown when the chart canvas is empty. Each
 * template emits a partial Plotly figure spec keyed by column-name `*src`
 * references; the parent hydrates them against the live data.
 *
 * Heuristics for choosing initial X / Y:
 *   - X: first column (typically the dimension / date)
 *   - Y: first numeric-looking column past index 0 (or column[1] as fallback)
 */
function QuickChartTemplates({ columns, onPick, onCustomize, disabled = false }) {
  const x = columns[0];
  const y = columns[1] || columns[0];
  // 3×3 grid of distinct chart types. KPI/metric is no longer surfaced here —
  // users pick "Metric" from the type switcher in the header instead.
  const TEMPLATES = [
    { id: 'bar',    icon: <BarChartOutlined />,  h: 'Bar',         spec: { data: [{ type: 'bar', xsrc: x, ysrc: y }], layout: { autosize: true } } },
    { id: 'hbar',   icon: <BarsOutlined />,      h: 'Horizontal',  spec: { data: [{ type: 'bar', orientation: 'h', xsrc: y, ysrc: x }], layout: { autosize: true } } },
    { id: 'line',   icon: <LineChartOutlined />, h: 'Line',        spec: { data: [{ type: 'scatter', mode: 'lines+markers', xsrc: x, ysrc: y }], layout: { autosize: true } } },
    { id: 'area',   icon: <AreaChartOutlined />, h: 'Area',        spec: { data: [{ type: 'scatter', mode: 'lines', fill: 'tozeroy', xsrc: x, ysrc: y }], layout: { autosize: true } } },
    { id: 'scat',   icon: <DotChartOutlined />,  h: 'Scatter',     spec: { data: [{ type: 'scatter', mode: 'markers', xsrc: x, ysrc: y }], layout: { autosize: true } } },
    { id: 'pie',    icon: <PieChartOutlined />,  h: 'Pie',         spec: { data: [{ type: 'pie', labelssrc: x, valuessrc: y }], layout: { autosize: true } } },
    { id: 'donut',  icon: <PieChartOutlined />,  h: 'Donut',       spec: { data: [{ type: 'pie', hole: 0.5, labelssrc: x, valuessrc: y }], layout: { autosize: true } } },
    { id: 'heat',   icon: <HeatMapOutlined />,   h: 'Heatmap',     spec: { data: [{ type: 'histogram2d', xsrc: x, ysrc: y }], layout: { autosize: true } } },
    { id: 'box',    icon: <BoxPlotOutlined />,   h: 'Box plot',    spec: { data: [{ type: 'box', ysrc: y }], layout: { autosize: true } } },
  ];
  return (
    <div className="dx-templates-wrap" style={{ padding: disabled ? 0 : 18, overflow: 'auto' }}>
      {!disabled && (
        <div className="dx-templates-head">
          <Typography.Text strong style={{ fontSize: 14 }}>Pick a chart type</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {x ? <>Defaults: <code>{x}</code> on X{y && y !== x ? <> · <code>{y}</code> on Y</> : null}</> : 'Run a query to populate columns'}
          </Typography.Text>
          <span style={{ flex: 1 }} />
          {onCustomize && <Button size="small" type="link" onClick={onCustomize}>Skip — open Plotly editor</Button>}
        </div>
      )}
      <div className="dx-templates">
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`dx-template${disabled ? ' disabled' : ''}`}
            disabled={disabled}
            onClick={() => { if (!disabled) onPick(t.spec); }}
          >
            <div className="icn">{t.icon}</div>
            <div className="h">{t.h}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
